import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { isLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import { playPairFailureSound } from "@/lib/floorStationPairAudio";
import { useLaborSession } from "@/lib/labor/useLaborSession";

import { claimInboundReceiveCrate, closeInboundContainerLaborVisit } from "./inboundApi";
import { inboundContainerLaborEnabled } from "./inboundContainerLabor";
import { inboundFloorLeavePlan, type InboundFloorLeaveReason } from "./inboundFloorLeave";
import { receivingLaborJobKeyFromWirePage } from "./inboundLaborPage";

type VisitState = {
  startedMs: number;
  lastActivityMs: number;
  armed: boolean;
};

type InboundContainerLaborContextValue = {
  markWrite: () => void;
};

const InboundContainerLaborContext = createContext<InboundContainerLaborContextValue>({
  markWrite: () => {},
});

export function useInboundContainerLaborWrite(): () => void {
  return useContext(InboundContainerLaborContext).markWrite;
}

function leaveInboundFloor(
  navigate: ReturnType<typeof useNavigate>,
  reason: InboundFloorLeaveReason,
): void {
  const plan = inboundFloorLeavePlan({
    reason,
    ownerDeparting: isLaborOwnerDeparting(),
  });
  if (plan.playFailureSound) {
    playPairFailureSound();
  }
  if (plan.navigateHome) {
    navigate("/", { replace: true });
  }
}

async function flushContainerVisit(input: {
  packageId: string;
  jobKey: string;
  visit: VisitState;
  idleTimeout: boolean;
}): Promise<void> {
  const parsedPackageId = Number.parseInt(input.packageId, 10);
  if (!Number.isSafeInteger(parsedPackageId) || parsedPackageId <= 0) return;
  try {
    await closeInboundContainerLaborVisit({
      inbound_arrival_package_id: parsedPackageId,
      job_key: input.jobKey,
      armed: input.visit.armed,
      visit_started_at_ms: input.visit.startedMs,
      last_activity_at_ms: input.visit.lastActivityMs,
      idle_timeout: input.idleTimeout,
    });
  } catch {
    // Visit close is best-effort on navigation; the next heartbeat may retry allocation.
  }
}

/**
 * Order-scoped receiving labor on the receive details page. The receive queue
 * overview does not clock in. On an order workspace the timer starts as soon as
 * the detail page mounts. Persisted writes arm the open dock crate visit so
 * elapsed session time can roll up to that crate (minus idle).
 */
export function InboundContainerLaborProvider(props: {
  arrivalPackageId: string | null;
  wirePage: string;
  workLabel: string;
  /** Optional ref populated with {@link markWrite} for parent callbacks (e.g. auto-save). */
  writeRef?: MutableRefObject<(() => void) | null>;
  children: ReactNode;
}): JSX.Element {
  const navigate = useNavigate();
  const visitRef = useRef<VisitState>({
    startedMs: Date.now(),
    lastActivityMs: Date.now(),
    armed: false,
  });
  const packageIdRef = useRef<string | null>(null);
  const jobKey = receivingLaborJobKeyFromWirePage(props.wirePage);
  const laborEnabled = inboundContainerLaborEnabled({
    arrivalPackageId: props.arrivalPackageId,
    wirePage: props.wirePage,
  });
  const laborEnabledRef = useRef(laborEnabled);

  const closeCurrentVisit = useCallback(
    (idleTimeout: boolean) => {
      const packageId = packageIdRef.current;
      if (packageId == null) return;
      void flushContainerVisit({
        packageId,
        jobKey,
        visit: visitRef.current,
        idleTimeout,
      });
    },
    [jobKey],
  );

  const laborSession = useLaborSession({
    page: props.wirePage,
    domain: "shipping",
    moduleGate: "shipping",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: props.workLabel,
    autoStart: laborEnabled,
    requirePersistedClockIn: true,
    onIdleTimeout: () => {
      closeCurrentVisit(true);
      leaveInboundFloor(navigate, "idle");
    },
    onPersistedSessionLost: () => {
      if (isLaborOwnerDeparting()) return;
      leaveInboundFloor(navigate, "session_lost");
    },
    onSessionEndedRemotely: () => {
      if (isLaborOwnerDeparting()) return;
      leaveInboundFloor(navigate, "ended_remotely");
    },
    onStopped: () => {
      if (isLaborOwnerDeparting()) return;
      leaveInboundFloor(navigate, "stopped");
    },
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Shipping, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  useLaborDevStatusPanelRegistration(
    import.meta.env.DEV,
    useMemo(
      (): LaborStatusPanelProps => ({
        isWorking: laborSession.isWorking,
        elapsedSeconds: laborSession.elapsedSeconds,
        workLabel: props.workLabel,
        banner: laborSession.banner,
        lastSummary: laborSession.lastSummary,
        heartbeatIntervalMs: laborSession.heartbeatIntervalMs,
        idleTimeoutMs: laborSession.idleTimeoutMs,
      }),
      [
        laborSession.isWorking,
        laborSession.elapsedSeconds,
        laborSession.banner,
        laborSession.lastSummary,
        laborSession.heartbeatIntervalMs,
        laborSession.idleTimeoutMs,
        props.workLabel,
      ],
    ),
  );

  const markWrite = useCallback(() => {
    visitRef.current.armed = true;
    visitRef.current.lastActivityMs = Date.now();
  }, []);

  useEffect(() => {
    if (props.writeRef == null) return;
    props.writeRef.current = markWrite;
    return () => {
      props.writeRef!.current = null;
    };
  }, [markWrite, props.writeRef]);

  useEffect(() => {
    if (laborEnabledRef.current && !laborEnabled && laborSession.isWorking) {
      if (!isLaborOwnerDeparting()) {
        void laborSession.stopWorking();
      }
    }
    laborEnabledRef.current = laborEnabled;
  }, [laborEnabled, laborSession.isWorking, laborSession.stopWorking]);

  useEffect(() => {
    if (!laborSession.isWorking || props.arrivalPackageId == null) return;
    const packageId = Number.parseInt(props.arrivalPackageId, 10);
    if (!Number.isSafeInteger(packageId) || packageId <= 0) return;
    void claimInboundReceiveCrate({
      inbound_arrival_package_id: packageId,
      job_key: jobKey,
    }).catch(() => undefined);
  }, [jobKey, laborSession.isWorking, props.arrivalPackageId]);

  useEffect(() => {
    if (props.arrivalPackageId == null) {
      packageIdRef.current = null;
      return;
    }
    if (packageIdRef.current != null && packageIdRef.current !== props.arrivalPackageId) {
      closeCurrentVisit(false);
    }
    packageIdRef.current = props.arrivalPackageId;
    visitRef.current = {
      startedMs: Date.now(),
      lastActivityMs: Date.now(),
      armed: false,
    };
    return () => {
      if (packageIdRef.current === props.arrivalPackageId) {
        closeCurrentVisit(false);
        packageIdRef.current = null;
      }
    };
  }, [closeCurrentVisit, props.arrivalPackageId]);

  const contextValue = useMemo(() => ({ markWrite }), [markWrite]);

  return (
    <InboundContainerLaborContext.Provider value={contextValue}>
      {props.children}
    </InboundContainerLaborContext.Provider>
  );
}
